// Admin: ERP↔MES integration outbox visibility + DLQ replay.
//
// Three tabs: pending / sent / dlq.  Stats card at the top.  DLQ rows
// get a "Requeue" button that resets attempt_count and bumps the row
// back to pending so the publisher will retry.
//
// Backend contract:
//   GET  /api/v1/admin/integrations/outbox?status=...&page=...
//   GET  /api/v1/admin/integrations/outbox/stats
//   POST /api/v1/admin/integrations/outbox/:id/requeue

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { adminIntegrationsApi, ApiError, manufacturingOrdersApi } from "@/lib/api";
import type {
  ErpPullSettings,
  ErpPullSettingsPatch,
  IntegrationOutboxRow,
  IntegrationOutboxStatus,
} from "@/lib/types";
import { formatDateTime } from "@/lib/format";

const PULL_INPUT_CLS =
  "w-full px-3 py-2 rounded-md bg-surface border border-border";

const TABS: { key: IntegrationOutboxStatus; label: string }[] = [
  { key: "dlq", label: "DLQ" },
  { key: "pending", label: "Pending" },
  { key: "sent", label: "Sent" },
];

export default function IntegrationsErp() {
  return (
    <RequirePermission codes="integration.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<IntegrationOutboxStatus>("dlq");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const statsQ = useQuery({
    queryKey: ["admin", "integrations", "stats"],
    queryFn: () => adminIntegrationsApi.stats(),
    refetchInterval: 10_000,
  });

  const listQ = useQuery({
    queryKey: ["admin", "integrations", "outbox", tab, page],
    queryFn: () =>
      adminIntegrationsApi.listOutbox({ status: tab, page, page_size: 50 }),
    refetchInterval: 10_000,
  });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminIntegrations")}
        title="ERP outbox"
        description={t("integrationsErp.description")}
      />

      <PullAdapterCard />

      <StatsCard
        loading={statsQ.isLoading}
        stats={statsQ.data ?? null}
      />

      <div className="mt-4 flex gap-2 border-b border-border">
        {TABS.map((tabDef) => (
          <button
            key={tabDef.key}
            onClick={() => {
              setTab(tabDef.key);
              setPage(1);
            }}
            className={
              "px-4 py-2 text-sm font-medium transition-colors " +
              (tab === tabDef.key
                ? "border-b-2 border-primary text-text-primary"
                : "text-text-muted hover:text-text-primary")
            }
          >
            {tabDef.label}
            {statsQ.data && (
              <span className="ml-2 text-xs text-text-muted">
                ({countForStatus(statsQ.data, tabDef.key)})
              </span>
            )}
          </button>
        ))}
      </div>

      {listQ.isLoading && <LoadingState />}
      {listQ.isError && <ErrorState error={listQ.error} />}

      {listQ.data && listQ.data.items.length === 0 && (
        <section className="card p-8 mt-4 text-center text-text-muted">
          {t("integrationsErp.emptyStatus", { status: tab })}
        </section>
      )}

      {listQ.data && listQ.data.items.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">event_type</th>
                <th className="px-3 py-2 text-left">aggregate_id</th>
                <th className="px-3 py-2 text-right">seq</th>
                <th className="px-3 py-2 text-right">attempts</th>
                <th className="px-3 py-2 text-left">scheduled_at</th>
                <th className="px-3 py-2 text-left">last_error</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.items.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  expanded={expanded.has(row.id)}
                  onToggle={() =>
                    setExpanded((cur) => {
                      const next = new Set(cur);
                      if (next.has(row.id)) next.delete(row.id);
                      else next.add(row.id);
                      return next;
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        </section>
      )}

      {listQ.data && listQ.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", { page: listQ.data.pagination.page, pages: listQ.data.pagination.pages, total: listQ.data.pagination.total })}
          </span>
          <div className="flex gap-2">
            <button
              className="btn btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ←
            </button>
            <button
              className="btn btn-secondary"
              disabled={page >= listQ.data.pagination.pages}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function StatsCard({
  loading,
  stats,
}: {
  loading: boolean;
  stats: { pending: number; sent: number; dlq: number; oldest_pending_age_sec: number | null } | null;
}) {
  if (loading) return <LoadingState />;
  if (!stats) return null;

  const ageAlarming =
    stats.oldest_pending_age_sec !== null && stats.oldest_pending_age_sec > 300;

  return (
    <section className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-4">
      <Stat label="Pending" value={stats.pending} />
      <Stat label="Sent" value={stats.sent} />
      <Stat
        label="DLQ"
        value={stats.dlq}
        alarming={stats.dlq > 0}
      />
      <Stat
        label="Oldest pending age"
        value={
          stats.oldest_pending_age_sec === null
            ? "—"
            : formatAge(stats.oldest_pending_age_sec)
        }
        alarming={ageAlarming}
      />
    </section>
  );
}

function Stat({
  label,
  value,
  alarming = false,
}: {
  label: string;
  value: number | string;
  alarming?: boolean;
}) {
  return (
    <div className="card p-4">
      <div className="text-xs text-text-muted">{label}</div>
      <div
        className={
          "text-2xl font-semibold mt-1 " +
          (alarming ? "text-red-500" : "text-text-primary")
        }
      >
        {value}
      </div>
    </div>
  );
}

function countForStatus(
  stats: { pending: number; sent: number; dlq: number },
  status: IntegrationOutboxStatus,
): number {
  switch (status) {
    case "pending": return stats.pending;
    case "sent":    return stats.sent;
    case "dlq":     return stats.dlq;
  }
}

function formatAge(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

function Row({
  row,
  expanded,
  onToggle,
}: {
  row: IntegrationOutboxRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const requeue = useMutation({
    mutationFn: () => adminIntegrationsApi.requeue(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "integrations"] });
    },
  });

  return (
    <>
      <tr
        className="border-t border-border cursor-pointer hover:bg-surface-alt"
        onClick={onToggle}
      >
        <td className="px-3 py-2 font-mono text-xs">{row.event_type}</td>
        <td className="px-3 py-2 font-mono text-xs text-text-muted">
          {row.aggregate_id.slice(0, 8)}…
        </td>
        <td className="px-3 py-2 text-right font-mono text-xs">{row.event_seq}</td>
        <td className="px-3 py-2 text-right font-mono text-xs">
          {row.attempt_count}
        </td>
        <td className="px-3 py-2 text-xs text-text-muted">
          {formatDateTime(row.scheduled_at)}
        </td>
        <td className="px-3 py-2 text-xs text-red-400 max-w-md truncate">
          {row.last_error ?? "—"}
        </td>
        <td className="px-3 py-2 text-right">
          {row.status === "dlq" && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                requeue.mutate();
              }}
              disabled={requeue.isPending}
            >
              {requeue.isPending ? "…" : "Requeue"}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-surface-alt">
          <td colSpan={7} className="px-3 py-3">
            {requeue.isError && (
              <div className="mb-3">
                <ErrorBanner>{(requeue.error as Error).message}</ErrorBanner>
              </div>
            )}
            <div className="text-xs text-text-muted mb-1">payload</div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-bg p-3 rounded border border-border">
              {JSON.stringify(row.payload, null, 2)}
            </pre>
            <div className="grid grid-cols-3 gap-3 mt-3 text-xs text-text-muted">
              <div>
                <div className="font-medium">id</div>
                <div className="font-mono">{row.id}</div>
              </div>
              <div>
                <div className="font-medium">created_at</div>
                <div className="font-mono">{formatDateTime(row.created_at)}</div>
              </div>
              <div>
                <div className="font-medium">sent_at</div>
                <div className="font-mono">
                  {row.sent_at ? formatDateTime(row.sent_at) : "—"}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================
// STAITER pull-адаптер — настройки приёма заказов из STAITER
// ============================================================

function PullAdapterCard() {
  const qc = useQueryClient();
  const settingsQ = useQuery({
    queryKey: ["admin", "integrations", "pull", "settings"],
    queryFn: () => adminIntegrationsApi.pullSettings(),
    refetchInterval: 15_000,
  });

  return (
    <section className="card p-5 mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg">Приём заказов из STAITER (pull-адаптер)</h2>
        {settingsQ.data && (
          <span
            className={
              "pill " + (settingsQ.data.enabled ? "pill-ok" : "pill-warn")
            }
          >
            {settingsQ.data.enabled ? "включён" : "выключен"}
          </span>
        )}
      </div>
      <p className="text-sm text-text-muted">
        MES сам опрашивает STAITER и создаёт «Заказ на производство» в DRAFT.
        STAITER не дорабатывается. Все параметры — в БД, правит администратор.
      </p>

      {settingsQ.isLoading && <LoadingState />}
      {settingsQ.isError && <ErrorState error={settingsQ.error} />}
      {settingsQ.data && (
        <PullForm
          key={settingsQ.data.updated_at}
          initial={settingsQ.data}
          onChanged={() =>
            void qc.invalidateQueries({
              queryKey: ["admin", "integrations", "pull", "settings"],
            })
          }
        />
      )}
    </section>
  );
}

function PullForm({
  initial,
  onChanged,
}: {
  initial: ErpPullSettings;
  onChanged: () => void;
}) {
  const [enabled, setEnabled] = useState(initial.enabled);
  const [baseUrl, setBaseUrl] = useState(initial.base_url ?? "");
  const [username, setUsername] = useState(initial.username ?? "");
  const [password, setPassword] = useState("");
  const [intervalMin, setIntervalMin] = useState(
    String(Math.max(1, Math.round(initial.interval_seconds / 60))),
  );
  const [statuses, setStatuses] = useState(initial.statuses);
  const [pageSize, setPageSize] = useState(String(initial.page_size));
  const [entityId, setEntityId] = useState(initial.entity_id ?? "");
  const [processKey, setProcessKey] = useState(initial.process_key ?? "");
  const [itemIds, setItemIds] = useState(initial.item_ids.join("\n"));
  const [err, setErr] = useState<string | null>(null);
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: () => {
      const patch: ErpPullSettingsPatch = {
        enabled,
        base_url: baseUrl.trim() || null,
        username: username.trim() || null,
        interval_seconds: Math.max(60, Math.round(Number(intervalMin) * 60)),
        statuses: statuses.trim() || "released",
        page_size: Math.min(200, Math.max(1, Math.round(Number(pageSize)) || 200)),
        entity_id: entityId.trim() || null,
        process_key: processKey || null,
        item_ids: itemIds.split(/[\s,]+/).filter(Boolean),
      };
      if (password) patch.password = password;
      return adminIntegrationsApi.updatePullSettings(patch);
    },
    onSuccess: () => {
      setPassword("");
      setErr(null);
      onChanged();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message : "Не удалось сохранить настройки."),
  });

  const runNow = useMutation({
    mutationFn: () => adminIntegrationsApi.runPullNow(),
    onSuccess: () => onChanged(),
  });

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Опрос включён (без галочки трафик к STAITER не идёт)
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Base URL (вкл. /api/v1)
          </span>
          <input
            className={`${PULL_INPUT_CLS} font-mono mt-1`}
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://staiter.example/api/v1"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Логин сервис-юзера (email)
          </span>
          <input
            className={`${PULL_INPUT_CLS} font-mono mt-1`}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="mes-integration@…"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Пароль {initial.password_set && "(задан — оставьте пустым, чтобы не менять)"}
          </span>
          <input
            type="password"
            className={`${PULL_INPUT_CLS} font-mono mt-1`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={initial.password_set ? "••••••••" : "введите пароль"}
            autoComplete="new-password"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Интервал опроса, мин (мин. 1)
          </span>
          <input
            type="number"
            min={1}
            className={`${PULL_INPUT_CLS} font-mono tabular-nums mt-1`}
            value={intervalMin}
            onChange={(e) => setIntervalMin(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Статусы WO (через запятую)
          </span>
          <input
            className={`${PULL_INPUT_CLS} font-mono mt-1`}
            value={statuses}
            onChange={(e) => setStatuses(e.target.value)}
            placeholder="released"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Размер страницы (≤200)
          </span>
          <input
            type="number"
            min={1}
            max={200}
            className={`${PULL_INPUT_CLS} font-mono tabular-nums mt-1`}
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            X-Active-Entity (если мультиэнтити)
          </span>
          <input
            className={`${PULL_INPUT_CLS} font-mono mt-1`}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            placeholder="— не задано —"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Техпроцесс принятых заказов
          </span>
          <select
            className={`${PULL_INPUT_CLS} mt-1`}
            value={processKey}
            onChange={(e) => setProcessKey(e.target.value)}
          >
            <option value="">— единственный техпроцесс установки —</option>
            {(processes.data ?? []).map((p) => (
              <option key={p.key} value={p.key}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Изделия STAITER (item_id по строке; пусто — все заказы)
          </span>
          <textarea
            className={`${PULL_INPUT_CLS} font-mono mt-1 min-h-[4rem]`}
            value={itemIds}
            onChange={(e) => setItemIds(e.target.value)}
            placeholder="— принимать заказы на любые изделия —"
          />
        </label>
      </div>

      {err && <p className="text-sm text-danger">{err}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Сохраняю…" : "Сохранить настройки"}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={runNow.isPending}
          onClick={() => runNow.mutate()}
          title="Запустить опрос немедленно, не дожидаясь интервала"
        >
          {runNow.isPending ? "Тяну…" : "Тянуть сейчас"}
        </button>
        {runNow.data && (
          <span className="text-sm text-text-muted">
            {runNow.data.error
              ? `ошибка: ${runNow.data.error}`
              : `просмотрено ${runNow.data.checked}, создано ${runNow.data.created}, пропущено ${runNow.data.skipped}, отменено ${runNow.data.cancelled}`}
          </span>
        )}
      </div>

      <div className="rounded-md border border-border bg-surface-alt/40 p-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
        <PullStat label="Последний опрос" value={initial.last_run_at ? formatDateTime(initial.last_run_at) : "—"} />
        <PullStat label="Последний успех" value={initial.last_success_at ? formatDateTime(initial.last_success_at) : "—"} />
        <PullStat label="Просм./создано/проп." value={`${initial.last_checked} / ${initial.last_created} / ${initial.last_skipped}`} />
        <PullStat label="Последняя ошибка" value={initial.last_error ?? "—"} danger={!!initial.last_error} />
      </div>
    </div>
  );
}

function PullStat({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</div>
      <div className={"mt-1 " + (danger ? "text-danger" : "")}>{value}</div>
    </div>
  );
}
